import React, { forwardRef, useCallback, useImperativeHandle, useMemo, useState } from "react";
import Select from "react-select";

import useProjectMeasureContext from "@/pages/takeoff/context/project-measure/use-project-measure-context.ts";
import useProjectIndicatorsContext from "@/pages/takeoff/context/project-indicators/use-project-indicators-context.ts";
import useProjectOrganisationContext from "@/pages/takeoff/context/project-organisation/use-project-organisation-context";

import Card from "@/components/card";
import SimpleLoadingIndicator from "@/components/simple-loading-indicator";
import CheckBox from "@/components/checkbox";

import MeasureType from "@/pages/takeoff/context/project-measure/models/measure-type.ts";

import { ExtractionViewerContainerRef } from "@/pages/takeoff/layouts/extraction/layouts/components/extraction-viewer-container.tsx";

interface SelectionCardProps {
    extractionViewerContainerRef: React.RefObject<ExtractionViewerContainerRef>;
    isViewerLoading?: boolean;
}

export interface SelectionCardRef {
    getSelectedMeasureType: () => MeasureType | null;
    getSelectedIndicatorDef: () => string | null;
    getSelectedStoreys: () => string[];
    setSelectedStoreys: (measureTypeId: string, storeyIds: string[]) => Promise<void>;
    setSelectedStoreysFrom3D: (indicatorDef: string, storeyIds: number[]) => Promise<void>;
}

interface SelectionOption {
    type: 'Measure' | 'Indicator';
    id: string;
    name: string;
}

const SelectionCard = forwardRef<SelectionCardRef, SelectionCardProps>(({ extractionViewerContainerRef, isViewerLoading }, ref) => {

    const { isLoading, projectMeasures, projectMeasureTypes } = useProjectMeasureContext();
    const { data: projectOrganisationData } = useProjectOrganisationContext();

    const { projectIndicatorsData } = useProjectIndicatorsContext();

    const orderedTypesNames = useMemo(() => {
        const types = projectMeasureTypes.sort((a, b) => a.name.localeCompare(b.name)).map(m => ({
            type: 'Measure',
            id: m.measureTypeId,
            name: m.name
        }) as SelectionOption);

        types.push({ type: 'Indicator', id: 'SHO', name: 'SHO' } as SelectionOption);
        types.push({ type: 'Indicator', id: 'SHOB', name: 'SHOB' } as SelectionOption);
        types.push({ type: 'Indicator', id: 'SDP', name: 'SDP' } as SelectionOption);
        types.push({ type: 'Indicator', id: 'SHAB', name: 'SHAB' } as SelectionOption);
        types.push({ type: 'Indicator', id: 'SFB', name: 'SFB' } as SelectionOption);

        return types;
    }, [projectMeasureTypes]);

    const [selectedOption, setSelectedOption] = useState<SelectionOption | null>(null);

    const [selectedStoreys, setSelectedStoreys] = useState<string[]>([]);

    const handle3DVisibilityFromBaamerStoreyId = useCallback (async (storeyId: string, visibility: boolean) => {
        if(selectedOption == null) return;
        const buildingAndStorey = projectMeasures.getBuildingAndStoreyFromStoreyId(storeyId);
        if (buildingAndStorey == null) return;
        const { building, storey } = buildingAndStorey;
        const idNumber = projectIndicatorsData?.buildings.find(b => b.buildingName === building.name)?.storeys.find(s => s.storeyName === storey.name)?.storeyId;
        if (idNumber == null) return;
        extractionViewerContainerRef.current?.handle3DVisibility(visibility, selectedOption.id, idNumber);
    }, [extractionViewerContainerRef, projectIndicatorsData?.buildings, projectMeasures, selectedOption]);

    const onOptionSelected = useCallback(async (option: SelectionOption | null) => {
        if (extractionViewerContainerRef.current == null || option == null) return;

        setSelectedOption(option);
        setSelectedStoreys([]);

        await extractionViewerContainerRef.current.clearGeometries();
        // if (option.type === 'Measure') {
        //     selectedStoreys.forEach(id => {
        //         extractionViewerContainerRef.current?.handleGeometriesVisibility(true, option.id, id);
        //     });
        // }
        // else {
        //     selectedStoreys.forEach(id => {
        //         handle3DVisibilityFromBaamerStoreyId(id, true);
        //     });
        // }
    }, [extractionViewerContainerRef]);

    const onStoreyChecked = useCallback(async (e: React.ChangeEvent<HTMLInputElement>, storeyId: string) => {
        if (extractionViewerContainerRef.current == null || selectedOption == null) return;

        const visibility = e.target.checked;
        
        if (visibility) {
            setSelectedStoreys(prev => [...prev, storeyId]);
        } else {
            setSelectedStoreys(prev => prev.filter(s => s !== storeyId));
        }

        if (selectedOption.type === 'Measure') {
            await extractionViewerContainerRef.current?.handleGeometriesVisibility(visibility, selectedOption.id, storeyId);
        }
        else {
            await handle3DVisibilityFromBaamerStoreyId(storeyId, visibility);
        }
        
    }, [selectedOption, extractionViewerContainerRef, handle3DVisibilityFromBaamerStoreyId]);

    useImperativeHandle(ref, () => ({
        getSelectedMeasureType: () => {
            if (selectedOption?.type === 'Measure') return projectMeasureTypes.find(mt => mt.measureTypeId === selectedOption.id) ?? null;
            return null;
        },
        getSelectedIndicatorDef: () => {
            if (selectedOption?.type === 'Indicator') return selectedOption.id;
            return null;
        },
        getSelectedStoreys: () => selectedStoreys,
        setSelectedStoreys: async (measureTypeId: string, storeyIds: string[]) => {
            if (extractionViewerContainerRef.current == null) return;
            await extractionViewerContainerRef.current.clearGeometries();

            const measureType = projectMeasureTypes.find(mt => mt.measureTypeId === measureTypeId);
            const data = measureType == null ? null : { type: 'Measure', id: measureType.measureTypeId, name: measureType.name } as SelectionOption;
            setSelectedOption(data ?? null);

            storeyIds.forEach(id => {
                // if (!selectedStoreys.includes(id)) {
                    extractionViewerContainerRef.current!.handleGeometriesVisibility(true, measureTypeId, id);
                // }
            });
            setSelectedStoreys(storeyIds);
        },
        setSelectedStoreysFrom3D: async (indicatorDef: string, storeyIds: number[]) => {
            if (extractionViewerContainerRef.current == null) return;
            const building = projectIndicatorsData?.buildings.find(b => b.storeys.some(s => storeyIds.includes(s.storeyId)));
            if (!building) return;

            const storeys = building.storeys.filter(s => storeyIds.includes(s.storeyId));
            const indicators = storeys.flatMap(s => s.indicators.filter(i => i.def.keyName === indicatorDef));
            if (!indicators) return;

            storeyIds.forEach(id => {
                extractionViewerContainerRef.current!.handle3DVisibility(true, indicatorDef, id);
            })

            const measureBuilding = projectMeasures.buildings.find(b => b.name === building.buildingName);
            const measureStoreys = measureBuilding?.storeys.filter(s => storeys.map(st => st.storeyName).includes(s.name));
            if (measureStoreys === undefined) return;

            const data = orderedTypesNames.find((type) => type.id === indicatorDef);
            setSelectedOption(data ?? null);
            setSelectedStoreys(measureStoreys.map(s => s.id));
        }
    }), [selectedOption, selectedStoreys, extractionViewerContainerRef, projectMeasureTypes, projectIndicatorsData?.buildings, projectMeasures.buildings, orderedTypesNames]);

    return (
        <Card title='Sélection' additionalHeaderClasses='uppercase font-semibold text-gray-800' additionalClasses='w-80 h-fit overflow-y-hidden max-h-full'>
            {isLoading ? (
                <SimpleLoadingIndicator />
            ) : (
                <div className='flex flex-col gap-y-2 py-2 overflow-y-hidden'>
                    <Select options={orderedTypesNames}
                        value={selectedOption}
                        isMulti={false}
                        getOptionValue={(option) => option.id}
                        getOptionLabel={(option) => option.name}
                        onChange={onOptionSelected}
                        isSearchable={false}
                        placeholder='Sélectionner un type de métré'
                        isDisabled={isViewerLoading}
                        className='px-4'
                        classNames={{
                            control: () => "!min-h-0 !rounded-md !border-gray-200",
                            menu: () => "text-sm",
                            menuList: () => "text-sm",
                            singleValue: () => "text-sm",
                            placeholder: () => "text-sm",
                            indicatorsContainer: () => "-my-1",
                        }}
                        menuPortalTarget={document.body}
                        menuPosition="fixed" />

                    {selectedOption ? (
                        <div className='overflow-y-auto max-h-full text-sm'>
                            {projectMeasures.buildings.map((building) => (
                                <div key={building.id} className='flex flex-col gap-y-2'>
                                    <div className='border-b pb-1'>
                                        <div className='font-semibold px-4'>{building.name}</div>
                                    </div>
                                    <div className='grid grid-cols-[auto_1fr_auto_auto] gap-x-4 px-4 gap-y-1'>
                                        {building.storeys.filter(s => projectOrganisationData?.getStoreyBuilding(building.id, s.id)?.files2d?.length ?? 0).map((storey) => {

                                            if (selectedOption.type === "Measure") {
                                                const storeyMeasures = storey.measures.filter(m => m.measureTypeId === selectedOption?.id);

                                                return (
                                                    <React.Fragment key={storey.id}>
                                                        <CheckBox className='my-auto' checked={selectedStoreys.includes(storey.id)} onChange={(e) => onStoreyChecked(e, storey.id)} disabled={isViewerLoading} />
                                                        <div>{storey.name}</div>
                                                        <div className='-mr-2 text-right'>{storeyMeasures.length}</div>
                                                        
                                                        <div className='mr-1'>u</div>
                                                    </React.Fragment>
                                                );
                                            }

                                            const indicators = projectIndicatorsData?.buildings.find(b => b.buildingName === building.name)
                                                ?.storeys.find(s => s.storeyName === storey.name)
                                                ?.indicators.filter(i => i.def.keyName === selectedOption?.id);

                                            return (
                                                <React.Fragment key={storey.id}>
                                                    <CheckBox className='my-auto'
                                                              checked={selectedStoreys.includes(storey.id)}
                                                              onChange={(e) => onStoreyChecked(e, storey.id)}
                                                              disabled={isViewerLoading}/>
                                                    <div>{storey.name}</div>
                                                    <div className='-mr-2 text-right'>{indicators?.length ?? 0}</div>
                                                    <div className='mr-1'>u</div>
                                                </React.Fragment>
                                            );
                                            
                                        })}
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className='px-4 py-2 text-sm text-gray-900 text-center'>Veuillez sélectionner un type de métré</div>
                    )}
                </div>
            )}
        </Card>
    )
});

export default SelectionCard;